import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, ChevronRight, MapPin, Users, DollarSign, Building2, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, UserCheck, TableProperties, Search, Pencil, Trash2, ExternalLink, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Accounts = ({ onLogout }) => {
  const [accounts, setAccounts] = useState([]);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const fileInputRef = useRef(null);

  // Click a column header: first click sorts A→Z, second click Z→A
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const POTENTIAL_RANK = { High: 1, Medium: 2, Low: 3 };
  const sortValue = (account, key) => {
    const c = account.contacts?.[0];
    switch (key) {
      case 'name': return (account.name || '').toLowerCase();
      case 'doctor': return (c?.name || '').toLowerCase();
      case 'mobile': return c?.whatsapp_number || '';
      case 'email': return (c?.email || '').toLowerCase();
      case 'city': return (account.city || '').toLowerCase().trim();
      case 'potential': return POTENTIAL_RANK[account.clinic_potential] || 9;
      case 'pending': return (hasIncompleteContacts(account) || !account.clinic_potential) ? 0 : 1;
      case 'created_by': return (account.created_by_name || account.created_by || '').toLowerCase();
      default: return '';
    }
  };

  const compareAccounts = (a, b) => {
    const va = sortValue(a, sortKey);
    const vb = sortValue(b, sortKey);
    // Blank values always go to the bottom, whichever direction
    const blankA = va === '' || va === 9;
    const blankB = vb === '' || vb === 9;
    if (blankA !== blankB) return blankA ? 1 : -1;
    let result = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
    if (result === 0) result = (a.name || '').localeCompare(b.name || '');
    return sortDir === 'asc' ? result : -result;
  };

  const SortHeader = ({ label, field }) => {
    const active = sortKey === field;
    return (
      <th
        onClick={() => handleSort(field)}
        title={`Sort by ${label}`}
        className={`sticky top-0 z-10 bg-slate-50 text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap hover:text-teal-700 ${active ? 'text-teal-700' : 'text-slate-500'}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active
            ? (sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />)
            : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
        </span>
      </th>
    );
  };
  const navigate = useNavigate();

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleDeleteAccount = async (e, account) => {
    e.stopPropagation(); // don't open account
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (!isAdmin) { toast.error('Only admins can delete accounts'); return; }
    if (!window.confirm(`⚠️ Delete "${account.name}"?\n\nThis permanently deletes the account and ALL its call history. Cannot be undone.`)) return;
    const typed = window.prompt(`Type the account name to confirm:\n\n"${account.name}"`);
    if (typed !== account.name) { toast.error('Name did not match — deletion cancelled'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/accounts/${account.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${account.name}" deleted ✅`);
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      toast.error('Please select an Excel (.xlsx) or CSV (.csv) file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/accounts/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      if (response.data.created > 0) {
        toast.success(`${response.data.created} accounts imported successfully!`);
        fetchAccounts();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to import file';
      toast.error(detail);
      setImportResult({ error: detail });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasIncompleteContacts = (account) => {
    return account.contacts?.some(c => !c.can_influence_decisions || !c.designation || c.designation === 'TBD' || !c.whatsapp_number) || !account.address || account.address === 'TBD';
  };


  const handleExportAccounts = () => {
    if (accounts.length === 0) { toast.error('No accounts to export'); return; }
    const rows = [
      ['Account ID', 'Account Name', 'Address', 'City', 'Pincode', 'Current Sales Value', 'Contact Name', 'Designation', 'Phone Number(s)', 'Email', 'Can Influence Decisions'],
    ];
    accounts.forEach(acc => {
      if (acc.contacts && acc.contacts.length > 0) {
        acc.contacts.forEach((c, i) => {
          rows.push([
            i === 0 ? (acc.account_id || '') : '',
            i === 0 ? acc.name : '',
            i === 0 ? (acc.address || '') : '',
            i === 0 ? (acc.city || '') : '',
            i === 0 ? (acc.pincode || '') : '',
            i === 0 ? (acc.current_sales_value || 0) : '',
            c.name || '',
            c.designation || '',
            c.whatsapp_number || '',
            c.email || '',
            c.can_influence_decisions || '',
          ]);
        });
      } else {
        rows.push([acc.account_id || '', acc.name, acc.address || '', acc.city || '', acc.pincode || '', acc.current_sales_value || 0, '', '', '', '', '']);
      }
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `VoiceCRM_Accounts_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Accounts exported successfully!');
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="accounts-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Accounts
            </h1>
            <p className="text-base text-slate-500 mt-2">Manage your customer accounts</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/accounts/bulk-edit')}
              variant="outline"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 rounded-xl px-5 py-3 font-semibold transition-all"
            >
              <TableProperties className="mr-2 h-4 w-4" />
              Edit Multiple
            </Button>
            <Button
              onClick={handleExportAccounts}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl px-5 py-3 font-semibold transition-all"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              onClick={() => { setImportResult(null); setShowImportModal(true); }}
              variant="outline"
              className="border-teal-300 text-teal-700 hover:bg-teal-50 rounded-xl px-5 py-3 font-semibold transition-all"
              data-testid="import-accounts-button"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Excel
            </Button>
            {isAdmin && (
              <Button
                onClick={() => navigate('/accounts/new')}
                className="bg-gradient-to-r from-teal-700 to-emerald-500 hover:from-teal-800 hover:to-emerald-600 text-white shadow-lg shadow-teal-500/25 rounded-xl px-6 py-3 font-semibold transition-all active:scale-[0.98]"
                data-testid="create-account-button"
              >
                <Plus className="mr-2 h-5 w-5" />
                New Account
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent"></div>
                <p className="text-slate-500 mt-4">Loading accounts...</p>
              </div>
            ) : accounts.length === 0 ? (
              <div className="text-center py-16">
                <div className="p-4 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full inline-block mb-4">
                  <Building2 className="h-10 w-10 text-teal-700" />
                </div>
                <p className="text-slate-600 text-lg font-medium mb-2">No accounts yet</p>
                <p className="text-slate-400 mb-6">Create your first account or import from Excel</p>
                <div className="flex items-center justify-center gap-3">
                  <Button
                    onClick={() => { setImportResult(null); setShowImportModal(true); }}
                    variant="outline"
                    className="border-teal-300 text-teal-700 hover:bg-teal-50 rounded-xl px-5 py-3"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Import Excel
                  </Button>
                  {isAdmin && (
                    <Button
                      onClick={() => navigate('/accounts/new')}
                      className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl px-6 py-3"
                    >
                      Create your first account
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <>
              {/* Search bar */}
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search accounts by name, city or contact..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 text-sm bg-white"
                />
              </div>

              {/* Tile Grid — 3 columns desktop, 2 tablet, 1 mobile */}
              {/* Scroll box: always fits the window, so the side and bottom scrollbars are always visible */}
              <div className="overflow-auto rounded-xl border border-slate-100" style={{ maxHeight: 'calc(100vh - 290px)' }}>
                <table className="w-full text-sm min-w-[1100px]">
                  <thead>
                    <tr className="border-b-2 border-slate-100">
                      <SortHeader label="Clinic Name" field="name" />
                      <SortHeader label="Doctor" field="doctor" />
                      <SortHeader label="Mobile" field="mobile" />
                      <SortHeader label="Email" field="email" />
                      <SortHeader label="City" field="city" />
                      <SortHeader label="Potential" field="potential" />
                      <SortHeader label="Pending Actions" field="pending" />
                      <SortHeader label="Created By" field="created_by" />
                      <th className="sticky top-0 z-10 bg-slate-50 py-3 px-4"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {[...accounts]
                      .filter(account => {
                        if (!searchQuery) return true;
                        const q = searchQuery.toLowerCase();
                        return (
                          account.name?.toLowerCase().includes(q) ||
                          account.city?.toLowerCase().includes(q) ||
                          account.address?.toLowerCase().includes(q) ||
                          account.contacts?.some(c => c.name?.toLowerCase().includes(q))
                        );
                      })
                      .sort(compareAccounts)
                      .map((account) => {
                        const incomplete = hasIncompleteContacts(account);
                        const firstContact = account.contacts?.[0];
                        const hasPendingActions = incomplete || !account.clinic_potential;
                        return (
                          <tr key={account.id}
                            onClick={() => navigate(`/accounts/${account.id}`)}
                            onContextMenu={e => { e.preventDefault(); window.open(`/accounts/${account.id}`, '_blank'); }}
                            className="hover:bg-teal-50/30 cursor-pointer transition-colors">
                            <td className="py-3 px-4">
                              <p className="font-semibold text-slate-800 text-sm">{account.name}</p>
                              <p className="text-xs text-slate-400 font-mono">{account.account_id}</p>
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm text-slate-700">{firstContact?.name ? `Dr. ${firstContact.name}` : '—'}</p>
                              <p className="text-xs text-slate-400">{firstContact?.designation || ''}</p>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">{firstContact?.whatsapp_number || '—'}</td>
                            <td className="py-3 px-4 text-xs text-slate-500">{firstContact?.email || '—'}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{account.city || '—'}</td>
                            <td className="py-3 px-4">
                              {account.clinic_potential ? (
                                <span className={`text-xs px-2 py-1 rounded-lg font-semibold ${
                                  account.clinic_potential === 'High' ? 'bg-emerald-100 text-emerald-700' :
                                  account.clinic_potential === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>{account.clinic_potential}</span>
                              ) : <span className="text-xs text-slate-300">Not set</span>}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-1 rounded-lg font-semibold ${
                                hasPendingActions ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                              }`}>{hasPendingActions ? '⚠️ Yes' : '✅ No'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-xs text-slate-600">{account.created_by_name || account.created_by?.split('@')[0] || '—'}</p>
                              <p className="text-xs text-slate-400">{account.created_by || ''}</p>
                            </td>
                            <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <button onClick={() => window.open(`/accounts/${account.id}`, '_blank')}
                                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </button>
                                {isAdmin && (
                                  <button onClick={e => handleDeleteAccount(e, account)}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>

        {/* Import Modal */}
        <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <FileSpreadsheet className="h-6 w-6 text-teal-700" />
                Import Accounts from Excel
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4">
              <div className="p-4 bg-teal-50 rounded-xl border border-teal-100 text-sm text-slate-700 space-y-2">
                <p className="font-semibold text-teal-800">Excel / CSV Format Guide:</p>
                <p>Your file should have columns like:</p>
                <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
                  <span className="bg-white px-2 py-1 rounded border border-teal-200 font-mono">Clinic Name</span>
                  <span className="text-slate-500">Account name</span>
                  <span className="bg-white px-2 py-1 rounded border border-teal-200 font-mono">Clinic Address</span>
                  <span className="text-slate-500">Account address</span>
                  <span className="bg-white px-2 py-1 rounded border border-teal-200 font-mono">City</span>
                  <span className="text-slate-500">City name</span>
                  <span className="bg-white px-2 py-1 rounded border border-teal-200 font-mono">Contact Name</span>
                  <span className="text-slate-500">Primary contact</span>
                  <span className="bg-white px-2 py-1 rounded border border-teal-200 font-mono">Mobile 1 / Mobile 2</span>
                  <span className="text-slate-500">All phone columns combined with /</span>
                  <span className="bg-white px-2 py-1 rounded border border-teal-200 font-mono">Email</span>
                  <span className="text-slate-500">Contact email (optional)</span>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Imported contacts will need "Designation", "Phone Number" and "Can Influence Decisions" fields filled in later. These accounts will show as pending tasks until completed.
                </p>
              </div>

              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImport}
                  className="hidden"
                  data-testid="excel-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="w-full h-24 border-2 border-dashed border-teal-300 bg-teal-50/50 hover:bg-teal-100/50 text-teal-700 rounded-xl flex flex-col items-center justify-center gap-2 transition-all"
                  variant="ghost"
                  data-testid="upload-excel-button"
                >
                  {importing ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-3 border-teal-500 border-t-transparent"></div>
                      <span className="text-sm font-semibold">Importing...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6" />
                      <span className="text-sm font-semibold">Click to select Excel or CSV file</span>
                    </>
                  )}
                </Button>
              </div>

              {importResult && !importResult.error && (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-2" data-testid="import-result">
                  <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                    <CheckCircle2 className="h-5 w-5" />
                    Import Complete
                  </div>
                  <div className="text-sm text-emerald-800 space-y-1">
                    <p>{importResult.created} account(s) created</p>
                    {importResult.skipped > 0 && <p>{importResult.skipped} row(s) skipped (empty name)</p>}
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 text-red-600">
                        <p className="font-medium">Errors:</p>
                        {importResult.errors.map((err, i) => <p key={i} className="text-xs">{err}</p>)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {importResult?.error && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
                  <p className="font-semibold">Import Failed</p>
                  <p>{importResult.error}</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Accounts;
